
import { defineComponent } from 'vue'
import { luxonFormatDate, formatDateWithLocale, localeDate, primeVueDate } from '@/helpers/commons/localeHelper'
import { setInputDataType, formatRegistryNumber } from '@/helpers/commons/tableHelpers'
import { AxiosResponse } from 'axios'
import Checkbox from 'primevue/checkbox'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import registryDescriptor from '../RegistryDescriptor.json'
import registryDatatableDescriptor from './RegistryDatatableDescriptor.json'
import RegistryDatatableEditableField from './RegistryDatatableEditableField.vue'
import RegistryDatatableWarningDialog from './RegistryDatatableWarningDialog.vue'

const deepcopy = require('deepcopy')

export default defineComponent({
    name: 'registry-datatable',
    components: {
        Checkbox,
        Column,
        DataTable,
        RegistryDatatableEditableField,
        RegistryDatatableWarningDialog
    },
    props: {
        propColumns: { type: Array },
        propRows: { type: Array, required: true },
        columnMap: { type: Object },
        propConfiguration: { type: Object },
        pagination: { type: Object },
        entity: { type: String },
        id: { type: String },
        stopWarningsState: { type: Array }
    },
    emits: ['rowChanged', 'rowDeleted', 'pageChanged', 'warningChanged'],
    data() {
        return {
            registryDescriptor,
            registryDatatableDescriptor,
            columns: [] as any[],
            rows: [] as any[],
            configuration: {} as any,
            comboColumnOptions: [] as any[],
            buttons: {
                enableButtons: false,
                enableDeleteRecords: false,
                enableAddRecords: false
            },
            lazyParams: {} as any,
            dependentColumns: [] as any[],
            selectedRow: null as any,
            warningVisible: false,
            stopWarnings: [] as any[],
            flagShown: 'flag-shown',
            flagHidden: 'flag-hidden',
            first: 0,
            loading: false,
            multiSortMeta: []
        }
    },
    watch: {
        propColumns() {
            this.loadColumns()
        },
        propRows: {
            handler() {
                this.loadRows()
            },
            deep: true
        },
        propConfiguration() {
            this.loadConfiguration()
        },
        pagination: {
            handler() {
                this.loadPagination()
                this.first = this.pagination?.start
            },
            deep: true
        }
    },
    created() {
        this.loadColumns()
        this.loadRows()
        this.loadConfiguration()
        this.loadPagination()
        this.loadWarningState()
    },
    computed: {
        getCurrentLocaleDefaultDateFormat() {
            return (column) => (column.isEditable ? column.format || primeVueDate() : localeDate())
        }
    },
    methods: {
        async loadColumns() {
            this.loading = true
            this.columns = [
                {
                    field: 'id',
                    title: '',
                    size: '',
                    isVisible: true,
                    isEditable: false,
                    columnInfo: { type: 'int' }
                }
            ]
            this.propColumns?.forEach((el: any) => {
                if (el.isVisible) this.columns.push(el)
            })
            this.setColumnDependencies()
            await this.loadInitialDropdownOptions()
            this.loading = false
        },
        setColumnDependencies() {
            this.columns.forEach((column: any) => {
                if (column.dependences) {
                    const index = this.columns.findIndex((parentColumn: any) => parentColumn.field === column.dependences)
                    if (index !== -1) {
                        this.columns[index].hasDependencies ? this.columns[index].hasDependencies.push(column) : (this.columns[index].hasDependencies = [column])
                        this.comboColumnOptions[column.dependences] = []
                    }
                }
            })
        },
        async loadInitialDropdownOptions() {
            for (let i = 0; i < this.columns.length; i++) {
                if (this.columns[i].editorType === 'COMBO') {
                    await this.addColumnOptions({ column: this.columns[i], row: {} })
                }
            }
        },
        loadRows() {
            this.rows = deepcopy(this.propRows)
        },
        loadConfiguration() {
            this.configuration = this.propConfiguration

            for (let i = 0; i < this.configuration.length; i++) {
                if (this.configuration[i].name === 'enableButtons') {
                    this.buttons.enableButtons = this.configuration[i].value === 'true'
                } else {
                    if (this.configuration[i].name === 'enableDeleteRecords') {
                        this.buttons.enableDeleteRecords = this.configuration[i].value === 'true'
                    }
                    if (this.configuration[i].name === 'enableAddRecords') {
                        this.buttons.enableAddRecords = this.configuration[i].value === 'true'
                    }
                }
            }
        },
        loadPagination() {
            this.lazyParams = { ...this.pagination } as any
        },
        loadWarningState() {
            this.stopWarnings = this.stopWarningsState as any[]
        },
        onPage(event: any) {
            this.lazyParams = {
                paginationStart: event.first,
                paginationLimit: event.rows,
                paginationEnd: event.first + event.rows,
                size: this.lazyParams.size
            }
            this.$emit('pageChanged', this.lazyParams)
        },
        rowDeleteConfirm(index: number, row: any) {
            this.$confirm.require({
                message: this.$t('common.toast.deleteMessage'),
                header: this.$t('common.toast.deleteTitle'),
                icon: 'pi pi-exclamation-triangle',
                accept: () => this.deleteRow(index, row)
            })
        },
        deleteRow(index: number, row: any) {
            row.isNew ? this.rows.splice(index, 1) : this.$emit('rowDeleted', row)
        },
        setDataType(columnType: string) {
            switch (columnType) {
                case 'int':
                case 'float':
                case 'decimal':
                case 'long':
                    return 'number'
                case 'date':
                    return 'date'
                default:
                    return 'text'
            }
        },
        getStep(dataType: string) {
            if (dataType === 'float') {
                return '.01'
            } else if (dataType === 'int') {
                return '1'
            } else {
                return 'any'
            }
        },
        getFormattedDate(date: any, format: any, incomingFormat?: string) {
            return luxonFormatDate(date, format, incomingFormat)
        },
        getFormattedDateTime(date: any, format?: any, keepNull?: boolean) {
            return formatDateWithLocale(date, format, keepNull)
        },
        async addColumnOptions(payload: any) {
            const column = payload.column
            const row = payload.row

            if (!this.comboColumnOptions[column.field]) {
                this.comboColumnOptions[column.field] = []
            }

            if (!this.comboColumnOptions[column.field][row[column.dependences]]) {
                await this.loadColumnOptions(column, row)
            }
        },
        async loadColumnOptions(column: any, row: any) {
            const subEntity = column.subEntity ? '::' + column.subEntity + '(' + column.foreignKey + ')' : ''

            const entityId = this.entity + subEntity + ':' + column.field
            const entityOrder = this.entity + subEntity + ':' + (column.orderBy ?? column.field)

            const postData = new URLSearchParams({
                ENTITY_ID: entityId,
                QUERY_TYPE: 'standard',
                ORDER_ENTITY: entityOrder,
                ORDER_TYPE: 'asc',
                QUERY_ROOT_ENTITY: 'true'
            })
            if (column.dependences && row && row[column.dependences]) {
                postData.append('DEPENDENCES', this.entity + subEntity + ':' + column.dependences + '=' + row[column.dependences])
            }
            await this.$http
                .post(`/knowageqbeengine/servlet/AdapterHTTP?ACTION_NAME=GET_FILTER_VALUES_ACTION&SBI_EXECUTION_ID=${this.id}`, postData, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
                .then((response: AxiosResponse<any>) => (this.comboColumnOptions[column.field][row[column.dependences] ?? 'All'] = response.data.rows))
        },
        addNewRow() {
            const newRow = { id: this.rows.length + 1, isNew: true }
            this.columns.forEach((el: any) => {
                if (el.isVisible && el.field !== 'id') {
                    newRow[el.field] = el.defaultValue ?? ''
                }
            })
            this.rows.unshift(newRow)

            if (this.lazyParams.size <= registryDescriptor.paginationLimit) {
                this.first = 0
            }
            this.$emit('rowChanged', newRow)
        },
        onDropdownChange(payload: any) {
            const column = payload.column
            const row = payload.row

            this.selectedRow = row
            if (column.hasDependencies) {
                this.dependentColumns = [] as any[]
                this.setDependentColumns(column)
                if (!this.stopWarnings[column.field]) {
                    this.dependentColumns.forEach((el: any) => {
                        if (this.selectedRow[el.field]) {
                            this.warningVisible = true
                        }
                    })
                } else {
                    this.clearDependentColumnsValues()
                }
            }

            this.setRowEdited(payload.row)
        },
        onWarningDialogClose(payload: any) {
            if (payload.stopWarnings) {
                this.stopWarnings[payload.columnField] = true
                this.$emit('warningChanged', this.stopWarnings)
            }

            this.clearDependentColumnsValues()
            this.warningVisible = false
        },
        clearDependentColumnsValues() {
            this.dependentColumns.forEach((el: any) => (this.selectedRow[el.field] = ''))
            this.selectedRow.edited = true
            this.$emit('rowChanged', this.selectedRow)
        },
        setDependentColumns(column: any) {
            let tempColumn = column

            if (!tempColumn.hasDependencies) {
                return
            }

            tempColumn.hasDependencies.forEach((el: any) => {
                this.dependentColumns.push(el)
                this.setDependentColumns(el)
            })
        },
        setRowEdited(row: any) {
            row.edited = true
            const index = this.rows.findIndex((tempRow: any) => tempRow.id === row.id)
            if (index !== -1) this.rows[index] = { ...row }
            this.$emit('rowChanged', row)
        },
        onCellEditComplete(event: any) {
            let id = event.newData.id
            if (id) {
                var foundIndex = this.rows.findIndex((x) => x.id == id)
                this.rows[foundIndex] = event.newData
            }
        },
        showDefaultNumberFormatIcon(column: any) {
            if (!column || !column.columnInfo || !column.format) return false
            const inputType = setInputDataType(column.columnInfo.type)
            if (inputType !== 'number') return false
            const configuration = formatRegistryNumber(column)
            return !configuration || (column.columnInfo.type === 'int' && !['####', '#,###', '#.###'].includes(column.format))
        },
        onSort(event: any) {
            this.multiSortMeta = event.multiSortMeta
        }
    }
})
